import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MessagingPlatform } from '@this+that/models';
import axios from 'axios';
import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import log from 'loglevel';

import { LabelColor } from '../../models';
import { updateLabel } from '../../services/labels.service';
import { updateFolder } from '../../services/outlookFolders.service';
import { useIntegrationsStore, useLabelsStore } from '../../store';
import { getHomeAccountId } from '../../utils/integrationAccountHelpers';
import { useCustomToast } from '../useCustomToast';

const DEBOUNCE_DELAY_MS = 300;

interface UpdateColorApiParams {
  id: string;
  emailAddress: string;
  color: LabelColor;
  previousColor: LabelColor | undefined;
  platform: MessagingPlatform;
  signal: AbortSignal;
}

export const useUpdateLabel = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();
  const { getLabelByIdAndEmailAddress, updateLabel: updateLabelState } =
    useLabelsStore();
  const { integrationAccounts } = useIntegrationsStore();
  const abortControllerRef = useRef<AbortController | null>(null);

  const executeColorUpdate = async ({
    id,
    emailAddress,
    color,
    previousColor,
    platform,
    signal,
  }: UpdateColorApiParams) => {
    try {
      if (platform === MessagingPlatform.GMAIL) {
        await updateLabel({
          emailAddress,
          labelId: id,
          color,
          signal,
        });
      } else if (platform === MessagingPlatform.OUTLOOK) {
        await updateFolder({
          platformAccountId: getHomeAccountId(
            emailAddress,
            integrationAccounts,
          ),
          labelId: id,
          signal,
        });
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      if (previousColor !== undefined) {
        updateLabelState(id, emailAddress, { color: previousColor });
      }
      log.error('Error updating label/folder color:', error);
      errorMessage(t('inbox.labels.updateLabelColorError'));
    }
  };

  const debouncedColorUpdateRef = useRef<DebouncedFunc<
    (params: UpdateColorApiParams) => Promise<void>
  > | null>(null);

  if (!debouncedColorUpdateRef.current) {
    debouncedColorUpdateRef.current = debounce(
      (params: UpdateColorApiParams) => executeColorUpdate(params),
      DEBOUNCE_DELAY_MS,
    );
  }

  useEffect(() => {
    return () => {
      debouncedColorUpdateRef.current?.cancel();
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleUpdateLabelColor = (
    id: string,
    emailAddress: string,
    color: LabelColor,
    platform: MessagingPlatform,
  ) => {
    const label = getLabelByIdAndEmailAddress(id, emailAddress);
    if (!label) {
      return;
    }

    const previousColor = 'color' in label ? label.color : undefined;
    updateLabelState(id, emailAddress, { color });

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    debouncedColorUpdateRef.current?.({
      id,
      emailAddress,
      color,
      previousColor,
      platform,
      signal: abortControllerRef.current.signal,
    });
  };

  const handleUpdateLabelName = async (
    id: string,
    name: string,
    emailAddress: string,
    platform: MessagingPlatform,
  ): Promise<boolean> => {
    const label = getLabelByIdAndEmailAddress(id, emailAddress);
    if (!label) {
      return false;
    }

    setIsLoading(true);
    const previousName = label.displayName;
    updateLabelState(id, emailAddress, { displayName: name });

    try {
      if (platform === MessagingPlatform.GMAIL) {
        await updateLabel({ emailAddress, labelId: id, name });
      } else if (platform === MessagingPlatform.OUTLOOK) {
        await updateFolder({
          platformAccountId: getHomeAccountId(
            emailAddress,
            integrationAccounts,
          ),
          labelId: id,
          name,
        });
      }
      return true;
    } catch (error) {
      updateLabelState(id, emailAddress, { displayName: previousName });
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        errorMessage(t('inbox.labels.createLabelModal.labelAlreadyExists'));
      } else {
        log.error('Error updating label/folder name:', error);
        errorMessage(t('inbox.labels.updateLabelNameError'));
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleUpdateLabelColor,
    handleUpdateLabelName,
    isLoading,
  };
};
